<script setup lang="ts">
import type { PublicLocality } from '~/modules/public/purchase/domain/public-purchase.types'
import { useCurrencyFormatter } from '~/shared-kernel/presentation/composables/useCurrencyFormatter'

defineProps<{
  localities: PublicLocality[]
  quantities: Record<string, number>
}>()

const emit = defineEmits<{ change: [localityId: string, quantity: number] }>()

const { t } = useI18n()
const { formatMoney } = useCurrencyFormatter()

// Going below the order minimum removes the line instead of leaving an invalid quantity.
function decrease(locality: PublicLocality, quantity: number) {
  emit('change', locality.id, quantity - 1 < (locality.minPerOrder ?? 1) ? 0 : quantity - 1)
}

function increase(locality: PublicLocality, quantity: number) {
  emit('change', locality.id, quantity === 0 ? (locality.minPerOrder ?? 1) : quantity + 1)
}

function limit(locality: PublicLocality) {
  return Math.min(locality.availableQuantity, locality.maxPerOrder ?? Number.POSITIVE_INFINITY)
}
</script>

<template>
  <div v-if="localities.length" class="public-purchase-quantities" data-purchase-tour="quantity">
    <div v-for="locality in localities" :key="locality.id" class="public-purchase-quantities__row" :data-locality-id="locality.id">
      <div class="public-purchase-quantities__info">
        <strong>{{ locality.name }}</strong>
        <span><b>{{ formatMoney(locality.price.amount, locality.price.currency) }}</b> · {{ t('public.purchase.available', { count: locality.availableQuantity }) }}</span>
      </div>
      <div class="public-purchase-quantities__stepper">
        <TqButton compact variant="ghost" icon="i-lucide-minus" :aria-label="`${t('public.purchase.decrease')}: ${locality.name}`" :disabled="!quantities[locality.id]" @click="decrease(locality, quantities[locality.id] ?? 0)" />
        <strong>{{ quantities[locality.id] ?? 0 }}</strong>
        <TqButton compact variant="ghost" icon="i-lucide-plus" :aria-label="`${t('public.purchase.increase')}: ${locality.name}`" :disabled="(quantities[locality.id] ?? 0) >= limit(locality)" @click="increase(locality, quantities[locality.id] ?? 0)" />
      </div>
    </div>
  </div>
</template>

<style scoped>
.public-purchase-quantities { display: grid; gap: .5rem; flex-shrink: 0; }
.public-purchase-quantities__row { display: flex; align-items: center; justify-content: space-between; gap: .5rem; padding: .45rem .6rem; border: 1px solid var(--tq-border); border-radius: var(--tq-radius-md); background: var(--tq-bg-elevated); }
.public-purchase-quantities__info { display: grid; gap: .1rem; min-width: 0; overflow-wrap: anywhere; }
.public-purchase-quantities__info span { color: var(--tq-text-soft); font-size: .8rem; }
.public-purchase-quantities__info b { color: var(--tq-text); font-weight: 600; }
.public-purchase-quantities__stepper { display: flex; flex-shrink: 0; align-items: center; gap: .2rem; }
.public-purchase-quantities__stepper strong { min-width: 1.5rem; text-align: center; }
</style>
